import { CircularProgress, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import React, { CSSProperties, useMemo } from 'react';
import { EntityUIComponentCollection, EntityUIComponentWithChildrenProps, EntitiesMapContainer, EntityExclusionCustomSettings,
    isEntityExclusionCustomSettings, filterEntityClassesByEntityClassesExclusionCustomSettings,
    filterEntityInstancesByEntityInstancesExclusionCustomSettings } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectAllEntityChildren, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';

const useStyle = makeStyles(() => {
    return {
        root: {
            height: 500,
        },
    };
}, { name: 'EntityChildrenMapUIComponent', index: 1 });


/**
 * Interface for EntityChildrenMapUIComponentCustomSettings's customSettings. These are the props/value that can be
 * configured for the 'EntityChildrenMapUIComponentCustomSettings' from the entityClass's uiSettings.
 */
export interface EntityChildrenMapUIComponentCustomSettings extends EntityExclusionCustomSettings {
    // Custom title and icon
    readonly titleKey?: string | undefined;
    readonly icon?: string | undefined;

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // Whether or not to include the children's alerting state info in the marker
    readonly showAlertingState?: boolean | undefined;

    // Whether or not to exclude children that don't have GPS coordinate. The default is false, all children will be displayed on the map.
    // Children without GPS coordinate will be displayed at lon/lat 0
    readonly excludeEntitiesWithoutLocation?: boolean | undefined;
}



/**
 * type guard function to check if an object is a type of EntityChildrenMapUIComponentCustomSettings
 */
const isEntityChildrenMapUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityChildrenMapUIComponentCustomSettings => {

    const settings = obj as EntityChildrenMapUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showAlertingState)) {
        return errorLogger?.logError({
            message: '"showAlertingState" must be undefined or a boolean.',
            value  : settings.showAlertingState,
            path   : [...configPath, 'showAlertingState'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.excludeEntitiesWithoutLocation)) {
        return errorLogger?.logError({
            message: '"excludeEntitiesWithoutLocation" must be undefined or a boolean.',
            value  : settings.excludeEntitiesWithoutLocation,
            path   : [...configPath, 'excludeEntitiesWithoutLocation'],
        }) ?? false;
    }

    if (!isEntityExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityChildrenMapUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly className?: string | undefined;
    readonly customSettings?: EntityChildrenMapUIComponentCustomSettings | undefined;
}


/**
 * This is a UI Component that can show its children on a Map
 */
export const EntityChildrenMapUIComponent: React.FC<EntityChildrenMapUIComponentProps> = (
    props: EntityChildrenMapUIComponentProps,
) => {
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();

    // Get all child entities
    const childEntities = useSelectAllEntityChildren(
        props.projectId,
        props.entityId,
    );

    // Filter out the entities that are NOT instances of the entity classes specified in includeEntityClasses
    const filteredChildEntities = useMemo(() => {
        if (childEntities) {
            const childrenFilteredByEntityClasses = filterEntityClassesByEntityClassesExclusionCustomSettings(childEntities, props.customSettings);
            return filterEntityInstancesByEntityInstancesExclusionCustomSettings(childrenFilteredByEntityClasses, props.customSettings);
        }

        return undefined;
    }, [childEntities, props.customSettings]);

    // Compile a list of entityIds to be passed to the EntitiesMapContainer
    const filteredChildEntityIds = useMemo(() => {
        return filteredChildEntities?.map((entity) => {
            return entity.entityId;
        });
    }, [filteredChildEntities]);


    return (
        <>
            {filteredChildEntityIds ? (
                <EntitiesMapContainer
                    className={clsx(props.className, classes.root)}
                    style={props.customSettings?.containerStyle}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : ''}
                    icon={props.customSettings?.icon}
                    projectId={props.projectId}
                    entityIds={filteredChildEntityIds}
                    showAlertingState={props.customSettings?.showAlertingState}
                    excludeEntitiesWithoutLocation={props.customSettings?.excludeEntitiesWithoutLocation}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityChildrenMapUIComponent', EntityChildrenMapUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Children_Map_Comp',
    {
        componentFunc          : EntityChildrenMapUIComponent,
        customSettingsValidator: isEntityChildrenMapUIComponentCustomSettings,
    },
);
